import { useEffect, useReducer, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiFetch, apiJson } from '@/lib/apiClient';
import type { ListenResponse, ListenStatus } from './types';

/**
 * The lifecycle of ONE Listen request, for one vehicle (slice 86, PLAN §3).
 *
 * 🔴 NEVER OPTIMISTIC. A 202 POST moves us to `sent` — the tracker was asked to
 * call, nothing more. ONLY a GET poll returning `state: 'accepted'` reaches
 * `accepted` ("Tracker accepted — calling you now"); a poll returning
 * `not_confirmed`/`failed`, OR the ~70 s deadline passing on a still-`sent` row,
 * reaches a terminal non-accepted state. The deadline is enforced on the CLIENT
 * too (not only trusted to the server sweep) so a stuck `sent` row can never
 * leave the UI claiming "Sent…" forever. We cannot observe the phone ring, so
 * "accepted" is the furthest the UI ever asserts.
 *
 * The POST's own refusals (offline / mic / role / rate-limited / invalid number
 * / dispatch failed) arrive as HTTP errors and surface as `phase: 'error'` with
 * a stable `errorCode` the dialog maps to a sentence.
 */

/** Every refusal the POST can hand back, flattened to one discriminator. */
export type ListenErrorCode =
  | 'not_found'
  | 'mic_not_fitted'
  | 'device_offline'
  | 'rejected_role'
  | 'read_only_session'
  | 'invalid_number'
  | 'rate_limited'
  | 'dispatch_failed'
  | 'unknown';

const KNOWN: readonly ListenErrorCode[] = [
  'not_found',
  'mic_not_fitted',
  'device_offline',
  'rejected_role',
  'read_only_session',
  'invalid_number',
  'rate_limited',
  'dispatch_failed',
] as const;

function normalise(value: string | null): ListenErrorCode {
  return value !== null && (KNOWN as readonly string[]).includes(value)
    ? (value as ListenErrorCode)
    : 'unknown';
}

class ListenError extends Error {
  constructor(readonly code: ListenErrorCode) {
    super(code);
    this.name = 'ListenError';
  }
}

/** ~70 s: the api confirmation watcher terminalises a stale `sent` row at 60 s,
 *  plus a poll of headroom so the client never flips to not-confirmed before the
 *  server has had its last word. */
const LISTEN_DEADLINE_MS = 70_000;
const POLL_MS = 2_000;

export type ListenPhase =
  | 'idle'
  | 'sending'
  | 'sent'
  | 'accepted'
  | 'notConfirmed'
  | 'failed'
  | 'error';

export interface ListenController {
  phase: ListenPhase;
  /** The number the tracker will ring, once known (from the 202 or the poll). */
  callbackPhone: string | null;
  /** The flattened refusal when `phase === 'error'`, else null. */
  errorCode: ListenErrorCode | null;
  /** Fire a Listen call. `id` is a saved number's id, or `null`/omitted for the
   *  caller's login phone (the POST omits `callbackNumberId`). */
  start: (id?: string | null) => void;
  reset: () => void;
}

interface Pending {
  requestId: string;
  deadline: number;
}

async function postListen(
  vehicleId: string,
  callbackNumberId: string | null,
): Promise<ListenResponse> {
  const res = await apiFetch(`/api/vehicles/${encodeURIComponent(vehicleId)}/listen`, {
    method: 'POST',
    // 🔴 OMIT the key for the default (login phone), never send `null` — the api
    // body is `{ callbackNumberId?: uuid }` and a `null` would fail its zod
    // uuid-optional check. A chosen id is sent as-is.
    body: JSON.stringify(callbackNumberId ? { callbackNumberId } : {}),
  });
  if (res.status === 202) return (await res.json()) as ListenResponse;
  let code: string | null = null;
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body?.error === 'string') code = body.error;
  } catch {
    /* no body → 'unknown' */
  }
  throw new ListenError(normalise(code));
}

export function useListen(vehicleId: string): ListenController {
  const [pending, setPending] = useState<Pending | null>(null);
  const [callbackPhone, setCallbackPhone] = useState<string | null>(null);
  // A tick that re-renders exactly at the deadline, so a request last polled as
  // `sent` just before it still flips to `notConfirmed`.
  const [, tick] = useReducer((n: number) => n + 1, 0);

  const mutation = useMutation({
    mutationFn: (callbackNumberId: string | null) => postListen(vehicleId, callbackNumberId),
    onSuccess: (resp) => {
      setCallbackPhone(resp.callbackPhone);
      setPending({ requestId: resp.requestId, deadline: Date.now() + LISTEN_DEADLINE_MS });
    },
  });

  const poll = useQuery({
    queryKey: ['listen', pending?.requestId],
    queryFn: () =>
      apiJson<ListenStatus>(`/api/listen/${encodeURIComponent(pending!.requestId)}`),
    enabled: pending !== null,
    // Poll while the row is still `sent` and the deadline has not passed. Any
    // terminal value, or the deadline, stops it — there is no infinite poll.
    refetchInterval: (query) => {
      const s = query.state.data?.state;
      if (s !== undefined && s !== 'sent') return false;
      if (pending !== null && Date.now() > pending.deadline) return false;
      return POLL_MS;
    },
    gcTime: 0,
    staleTime: 0,
  });

  // Force one render at the deadline — a poll may have last returned `sent` just
  // before it, and refetchInterval would then stop without another render.
  useEffect(() => {
    if (pending === null) return;
    const ms = pending.deadline - Date.now();
    if (ms <= 0) return;
    const id = setTimeout(() => tick(), ms + 50);
    return () => clearTimeout(id);
  }, [pending]);

  // Keep the phone from the poll too (the 202 already set it; the poll carries it
  // as well and may be the only source after a remount).
  const polled = poll.data?.callbackPhone ?? null;
  useEffect(() => {
    if (polled) setCallbackPhone((prev) => prev ?? polled);
  }, [polled]);

  let phase: ListenPhase = 'idle';
  if (mutation.isPending) {
    phase = 'sending';
  } else if (mutation.isError) {
    phase = 'error';
  } else if (pending !== null) {
    const state = poll.data?.state;
    const pastDeadline = Date.now() > pending.deadline;
    if (state === 'accepted') phase = 'accepted';
    else if (state === 'not_confirmed') phase = 'notConfirmed';
    else if (state === 'failed') phase = 'failed';
    else if (pastDeadline) phase = 'notConfirmed';
    else phase = 'sent';
  }

  const errorCode: ListenErrorCode | null =
    mutation.error instanceof ListenError
      ? mutation.error.code
      : mutation.isError
        ? 'unknown'
        : null;

  return {
    phase,
    callbackPhone,
    errorCode,
    start: (id) => mutation.mutate(id ?? null),
    reset: () => {
      setPending(null);
      setCallbackPhone(null);
      mutation.reset();
    },
  };
}
